import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';

const NotificationManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'admin',
    target: 'all' // 'all' or 'specific'
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('type', '==', 'admin'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const notificationsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setNotifications(notificationsList);
      console.log('Fetched admin notifications:', notificationsList.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newNotification.title || !newNotification.message) {
      setError('Please fill in all required fields');
      return;
    }

    if (newNotification.target === 'specific' && selectedUsers.length === 0) {
      setError('Please select at least one user for specific notifications');
      return;
    }

    try {
      const notificationsRef = collection(db, 'notifications');
      
      if (newNotification.target === 'all') {
        // Send to all users
        await addDoc(notificationsRef, {
          ...newNotification,
          userId: 'all',
          createdAt: serverTimestamp(),
          read: false,
          dismissed: false
        });
      } else {
        // Send to specific users
        const promises = selectedUsers.map(userId =>
          addDoc(notificationsRef, {
            ...newNotification,
            userId,
            createdAt: serverTimestamp(),
            read: false,
            dismissed: false
          })
        );
        await Promise.all(promises);
      }

      setNewNotification({
        title: '',
        message: '',
        type: 'admin',
        target: 'all'
      });
      setSelectedUsers([]);
      await fetchNotifications();
      console.log('Notification(s) sent successfully');
    } catch (error) {
      console.error('Error sending notification:', error);
      setError('Failed to send notification');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      await fetchNotifications();
      console.log('Notification deleted successfully');
    } catch (error) {
      console.error('Error deleting notification:', error);
      setError('Failed to delete notification');
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Notification Management</h2>

      {/* Create New Notification Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h3 className="text-lg font-semibold mb-4">Send New Notification</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={newNotification.title}
              onChange={(e) =>
                setNewNotification({ ...newNotification, title: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={newNotification.message}
              onChange={(e) =>
                setNewNotification({ ...newNotification, message: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
              rows="4"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Audience
            </label>
            <select
              value={newNotification.target}
              onChange={(e) =>
                setNewNotification({ ...newNotification, target: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
            >
              <option value="all">All Users</option>
              <option value="specific">Specific Users</option>
            </select>
          </div>

          {newNotification.target === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Users
              </label>
              <select
                multiple
                value={selectedUsers}
                onChange={(e) =>
                  setSelectedUsers(
                    Array.from(e.target.selectedOptions, (option) => option.value)
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.email}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <button
            type="submit"
            className="bg-[rgb(130,88,18)] text-white px-4 py-2 rounded-md hover:bg-[rgb(110,68,0)] transition-colors duration-300"
          >
            Send Notification
          </button>
        </form>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Notifications</h3>
        {loading ? (
          <div className="text-center py-4">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No notifications sent yet
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="border border-gray-200 rounded-md p-4"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{notification.title}</h4>
                    <p className="text-gray-600 mt-1">{notification.message}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Sent to: {notification.userId === 'all' ? 'All Users' : 'Specific Users'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {notification.createdAt?.toDate().toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(notification.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationManagement; 